import { useEffect, useRef, useState } from "react";
import axios, { API } from "../api";
import { playBase64Mp3 } from "../voiceUtils";
import { SnapShareButton } from "./SnapShareButton";

/**
 * Today's "Aura Check-in" card — appears once per day when there's an unseen greeting.
 * Auto-attempts to play the audio after user interaction; falls back to a Play button.
 *
 * Phase 25.6 — if audio_base64 came back null (TTS was offline at generation time),
 * we now show a "Generate audio" button that hits /api/checkin/regenerate-audio
 * instead of leaving Play disabled with no escape.
 */
export function DailyCheckinCard({ checkin: initialCheckin, onDismiss }) {
  const [checkin, setCheckin] = useState(initialCheckin);
  const [playing, setPlaying] = useState(false);
  const [played, setPlayed] = useState(false);
  const [error, setError] = useState("");
  const [regenerating, setRegenerating] = useState(false);
  const dismissedRef = useRef(false);

  useEffect(() => { setCheckin(initialCheckin); }, [initialCheckin]);

  useEffect(() => {
    // Attempt one auto-play; browsers will block it without a user gesture and we'll show Play.
    let cancelled = false;
    (async () => {
      if (!checkin?.audio_base64) return;
      try {
        setPlaying(true);
        await playBase64Mp3(checkin.audio_base64);
        if (!cancelled) { setPlayed(true); setPlaying(false); }
      } catch (e) {
        if (!cancelled) { setPlaying(false); setError("autoplay-blocked"); }
      }
    })();
    return () => { cancelled = true; };
  }, [checkin?.audio_base64]);

  const manualPlay = async () => {
    if (!checkin?.audio_base64) return;
    setError("");
    setPlaying(true);
    try {
      await playBase64Mp3(checkin.audio_base64);
      setPlayed(true);
    } catch (e) {
      setError("Audio failed to play");
    } finally {
      setPlaying(false);
    }
  };

  const dismiss = () => {
    if (dismissedRef.current) return;
    dismissedRef.current = true;
    onDismiss();
  };

  if (!checkin) return null;

  return (
    <div className="checkin-card" data-testid="checkin-card">
      <button
        className="checkin-close"
        onClick={dismiss}
        aria-label="Dismiss"
        data-testid="checkin-close-btn"
      >×</button>
      <div className="checkin-header">
        <span className="checkin-mood-emoji" aria-hidden>{checkin.mood_emoji}</span>
        <div className="checkin-header-text">
          <strong>Aura's Check-in</strong>
          <span>Feeling {checkin.mood} today</span>
        </div>
      </div>
      <p className="checkin-greeting">"{checkin.greeting_text}"</p>
      <div className="checkin-actions">
        {!checkin.audio_base64 ? (
          <button
            className="checkin-play-btn"
            onClick={async () => {
              setRegenerating(true);
              setError("");
              try {
                const res = await axios.post(`${API}/checkin/regenerate-audio`);
                if (res.data?.audio_base64) {
                  setCheckin((c) => ({ ...c, audio_base64: res.data.audio_base64 }));
                  // Auto-play after generating since the user just tapped intentionally.
                  setPlaying(true);
                  try {
                    await playBase64Mp3(res.data.audio_base64);
                    setPlayed(true);
                  } finally {
                    setPlaying(false);
                  }
                }
              } catch (e) {
                setError("Couldn't generate audio — try again in a sec.");
              } finally {
                setRegenerating(false);
              }
            }}
            disabled={regenerating}
            data-testid="checkin-regen-btn"
          >
            {regenerating ? "🎙️ Generating…" : "🎙️ Generate audio"}
          </button>
        ) : (
          <button
            className="checkin-play-btn"
            onClick={manualPlay}
            disabled={playing}
            data-testid="checkin-play-btn"
          >
            {playing ? "▶ Playing..." : played ? "▶ Replay" : "▶ Play"}
          </button>
        )}
        <button
          className="checkin-dismiss-btn"
          onClick={dismiss}
          data-testid="checkin-dismiss-btn"
        >
          {played ? "Got it" : "Skip"}
        </button>
      </div>
      {error === "autoplay-blocked" && !played && (
        <p className="checkin-hint">Tap Play to hear Aura</p>
      )}
      {checkin?.id && (
        <div className="checkin-share-row" data-testid="checkin-share-row">
          <SnapShareButton
            shareUrl={`${window.location.origin}/api/share/checkin/${checkin.id}`}
            label="Share this moment"
            testId="checkin-snap-share"
          />
        </div>
      )}
    </div>
  );
}
